import { Component, EventEmitter, Output, ChangeDetectorRef, OnInit, ViewChild, OnDestroy, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EfileAdminService } from '../efile-admin.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import {
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  setDropdownAriaLabel
} from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-efile-add-edit-diagnostics',
    templateUrl: './efile-add-edit-diagnostics.component.html',
    styleUrls: ['./efile-add-edit-diagnostics.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfileAddEditDiagnosticsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  addNew = false;
  /** Preserve edit-mode FORM so Form dropdown shows row value after formOptions load (not first option). */
  private initialFormValue = '';

  private initDone = false;

  title = 'Add/Edit Diagnostics Config';
  minimise = false;
  saving = false;
  showFormLoader = true;
  formsForEfileDiagnosticConfig: string[] = [];
  reverseAcctForEfileDiagnosticConfig: string[] = [];

  // p-dropdown options: { label, value } for consistent binding and display
  formOptions: { label: string; value: string }[] = [];
  /** Form dropdown bound value; kept in sync with rowData.FORM so edit mode shows row value, not first option */
  selectedForm: string = '';
  selectedFormItem: any = null;
  selectedRevAcctItem: any = null;
  selectedCalcAcctItem: any = null;
  selectedCalcTypeItem: any = null;
  selectedExcludeAcctItem: any = null;
  selectedActiveItem: any = null;
  reverseAcctOptions: { label: string; value: string }[] = [];
  calcTypeOptions: { label: string; value: string }[] = [
    { label: 'DIVIDED', value: 'DIVIDED' },
    { label: 'MULTIPLY', value: 'MULTIPLY' },
    { label: 'PERCENTAGE', value: 'PERCENTAGE' }
  ];
  activeOptions: { label: string; value: string }[] = [
    { label: 'Y', value: 'Y' },
    { label: 'N', value: 'N' }
  ];

  @ViewChild('diagForm') dataServiceForm!: NgForm;

  constructor(
    private efileAdminService: EfileAdminService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.initDone = false;
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    const r = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.syncRowDataFromPayload(r);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private syncRowDataFromPayload(r: any): void {
    try {
      const data = r && typeof r === 'object' && !Array.isArray(r) ? { ...r } : {};
      this.addNew = !r || Object.keys(data).length === 0;
      if (data && Object.keys(data).length > 0) {
        data.FORM = this.addNew ? '' : String(data.FORM ?? '').trim();
        data.REV_ACCT = this.addNew ? '' : String(data.REV_ACCT ?? '').trim();
        data.CALC_ACCT = this.addNew ? '' : String(data.CALC_ACCT ?? '').trim();
        data.EXCLUDE_ACCT = this.addNew ? '' : String(data.EXCLUDE_ACCT ?? '').trim();
        data.CALC_TYPE = this.addNew ? '' : String(data.CALC_TYPE ?? '').trim();
        data.IS_ACTIVE = this.addNew ? '' : String(data.IS_ACTIVE ?? '').trim();
        data.TAX_YEAR = String(data.TAX_YEAR || this.filterParams?.tax_year || '').trim();
      }
      this.rowData = data;
      if (!this.addNew && this.rowData?.FORM) {
        this.initialFormValue = String(this.rowData.FORM).trim();
        this.selectedForm = this.initialFormValue;
        if (this.formOptions.length > 0) {
          this.applyFormValueFromRowData();
        }
      } else {
        this.initialFormValue = '';
        this.selectedForm = '';
      }
      if (!this.addNew && this.rowData?.FORM && this.GENERAL_CONFIG?.base_url && this.filterParams && Object.keys(this.filterParams).length > 0) {
        this.loadReverseAcct(String(this.rowData.FORM).trim());
      }
    } catch (_) {
      this.rowData = {};
      this.addNew = true;
      this.selectedForm = '';
    }
  }

  /** Run init once when filterParams has a value (inputs may arrive after ngOnInit). */
  private tryInit(): void {
    if (!this.filterParams || Object.keys(this.filterParams).length === 0 || this.initDone) {
      return;
    }
    this.initDone = true;
    if (this.rowData && !this.rowData.TAX_YEAR && this.filterParams?.tax_year) {
      this.rowData.TAX_YEAR = this.filterParams.tax_year;
    }
    this.loadForms();
    if (!this.addNew && this.rowData?.FORM) {
      this.loadReverseAcct(this.rowData.FORM);
    }
  }

  getAcctPlaceholderText(): string {
    return this.reverseAcctOptions.length > 0
      ? 'Search...'
      : 'Select a form to enable this field (specific forms only)';
  }

  /**
   * Ensure Form dropdown shows rowData.FORM in edit mode.
   * Called after formOptions are loaded so the value can be matched against available options.
   */
  private applyFormValueFromRowData(): void {
    const formVal = this.initialFormValue;
    if (!formVal || this.addNew || this.formOptions.length === 0) return;
    const match = this.formOptions.find(o => String(o.value ?? '').trim() === formVal);
    if (!match) {
      this.formOptions = [{ label: formVal, value: formVal }, ...this.formOptions];
    }
    this.selectedForm = match ? match.value : formVal;
    this.rowData.FORM = this.selectedForm;
    this.selectedFormItem = findAppkitItemByValue(this.formOptions, this.selectedForm);
    this.syncValueDropdownItems();
  }

  private syncValueDropdownItems(): void {
    this.selectedRevAcctItem = findAppkitItemByValue(this.reverseAcctOptions, this.rowData?.REV_ACCT);
    this.selectedCalcAcctItem = findAppkitItemByValue(this.reverseAcctOptions, this.rowData?.CALC_ACCT);
    this.selectedExcludeAcctItem = findAppkitItemByValue(this.reverseAcctOptions, this.rowData?.EXCLUDE_ACCT);
    this.selectedCalcTypeItem = findAppkitItemByValue(this.calcTypeOptions, this.rowData?.CALC_TYPE);
    this.selectedActiveItem = findAppkitItemByValue(this.activeOptions, this.rowData?.IS_ACTIVE);
  }

  /** Build payload params for diagnostic config APIs from filterParams and globalParams only (no hardcoded values). */
  private getDiagnosticConfigParams(): Record<string, any> {
    const g = this.globalService || {};
    const f = this.filterParams || {};
    const issueId = g.issue_id_for_tx_save !== undefined && g.issue_id_for_tx_save !== ''
      ? g.issue_id_for_tx_save
      : g.issue_id;
    const params: Record<string, any> = {};
    const set = (key: string, val: any) => { if (val !== undefined && val !== null) params[key] = val; };
    set('activity_key', f.activity_key ?? g.activity_key);
    set('is_issue_key_enabled', f.is_issue_key_enabled ?? g.is_issue_key_enabled);
    set('is_locked', f.is_locked ?? g.is_locked);
    set('issue_id', issueId);
    set('issue_short_desc', f.issue_short_desc ?? g.issue_short_desc);
    set('jcd_key', f.jcd_key ?? g.jcd_key);
    set('jcd_ta_key', f.jcd_ta_key ?? g.jcd_ta_key);
    set('scenario', f.scenario ?? g.scenario);
    set('scenarioCode', f.scenarioCode ?? g.scenarioCode);
    set('scenario_v1', f.scenario_v1 ?? f.scenario ?? g.scenario);
    set('screen_key', f.screen_key ?? g.screen_key);
    set('screen_type', f.screen_type ?? g.screen_type);
    set('tax_year', f.tax_year ?? g.tax_year);
    return params;
  }

  loadForms(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url ?? '';
    if (!baseUrl) return;
    const params = this.getDiagnosticConfigParams();
    this.efileAdminService.getFormsForEfileDiagnosticConfig(baseUrl, params, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const items = res?.jsonObject || [];
        const raw = Array.isArray(items)
          ? items.map((i: any) => String(i.FORM ?? '').trim()).filter(Boolean)
          : [];
        this.formsForEfileDiagnosticConfig = raw;
        this.formOptions = raw.map(f => ({ label: f, value: f }));
        if (!this.addNew && this.initialFormValue) {
          this.applyFormValueFromRowData();
        } else {
          this.syncValueDropdownItems();
        }
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.formsForEfileDiagnosticConfig = [];
        this.formOptions = [];
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadReverseAcct(form: string): void {
    if (!form) {
      this.reverseAcctForEfileDiagnosticConfig = [];
      this.reverseAcctOptions = [];
      this.cdr.detectChanges();
      return;
    }
    const baseUrl = this.GENERAL_CONFIG?.base_url ?? '';
    if (!baseUrl) return;
    const params = this.getDiagnosticConfigParams();
    this.efileAdminService.getReverseAcctForEfileDiagnosticConfig(baseUrl, String(form).trim(), params, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const items = res?.jsonObject || [];
        const raw = Array.isArray(items)
          ? items.map((i: any) => String(i.FROM_ACCT ?? '').trim()).filter(Boolean)
          : [];
        this.reverseAcctForEfileDiagnosticConfig = raw;
        this.reverseAcctOptions = raw.map(a => ({ label: a, value: a }));
        // Edit mode only: ensure current REV_ACCT, CALC_ACCT, EXCLUDE_ACCT are in options so they display in dropdowns
        if (!this.addNew && this.rowData) {
          const toAdd: { label: string; value: string }[] = [];
          for (const key of ['REV_ACCT', 'CALC_ACCT', 'EXCLUDE_ACCT'] as const) {
            const val = this.rowData[key] ? String(this.rowData[key]).trim() : '';
            if (val && !this.reverseAcctOptions.some(o => o.value === val)) {
              toAdd.push({ label: val, value: val });
            }
          }
          if (toAdd.length > 0) {
            this.reverseAcctOptions = [...toAdd, ...this.reverseAcctOptions];
          }
        }
        this.syncValueDropdownItems();
        this.cdr.detectChanges();
      },
      error: () => {
        this.reverseAcctForEfileDiagnosticConfig = [];
        this.reverseAcctOptions = [];
        this.cdr.detectChanges();
      }
    });
  }

  onFormDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedFormItem = selected;
    this.onFormModelChange(selected?.value ?? null);
  }

  onFormSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedFormItem = selected;
    }
  }

  onValueDropdownSelect(event: any, field: string): void {
    const selected = getAppkitDropdownSelection(event);
    this.setValueDropdownItem(field, selected);
    this.rowData[field] = selected?.value ?? '';
    this.cdr.detectChanges();
  }

  onValueSelectItem(event: any, field: string): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.setValueDropdownItem(field, selected);
    }
  }

  private setValueDropdownItem(field: string, selected: any): void {
    switch (field) {
      case 'REV_ACCT': this.selectedRevAcctItem = selected; break;
      case 'CALC_ACCT': this.selectedCalcAcctItem = selected; break;
      case 'EXCLUDE_ACCT': this.selectedExcludeAcctItem = selected; break;
      case 'CALC_TYPE': this.selectedCalcTypeItem = selected; break;
      case 'IS_ACTIVE': this.selectedActiveItem = selected; break;
    }
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  /** Called when user picks a different form in the dropdown. */
  onFormModelChange(value: string | null): void {
    const form = String(value ?? '').trim();
    this.selectedForm = form;
    this.rowData.FORM = form;
    this.rowData.REV_ACCT = '';
    this.rowData.CALC_ACCT = '';
    this.rowData.EXCLUDE_ACCT = '';
    this.reverseAcctOptions = [];
    this.reverseAcctForEfileDiagnosticConfig = [];
    this.selectedRevAcctItem = null;
    this.selectedCalcAcctItem = null;
    this.selectedExcludeAcctItem = null;
    this.cdr.detectChanges();
    if (form) {
      this.loadReverseAcct(form);
    }
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {
    if (this.saving) return;
    if (this.dataServiceForm?.invalid) {
      this.gtwSnackbarService.openSnackBar('Please fill all required fields.', 'gtw-snackbar--red');
      return;
    }
    this.saving = true;
    this.cdr.detectChanges();

    const g = this.globalService || {};
    const f = this.filterParams || {};
    const issueId = g.issue_id_for_tx_save !== undefined && g.issue_id_for_tx_save !== ''
      ? g.issue_id_for_tx_save
      : g.issue_id;

    const requestPayload = {
      FORM: this.rowData?.FORM ?? '',
      REV_ACCT: this.rowData?.REV_ACCT ?? '',
      CALC_ACCT: this.rowData?.CALC_ACCT ?? '',
      EXCLUDE_ACCT: this.rowData?.EXCLUDE_ACCT ?? '',
      TAX_YEAR: this.rowData?.TAX_YEAR ?? f.tax_year ?? g.tax_year ?? '',
      CALC_NUM: this.rowData?.CALC_NUM ?? '',
      CALC_TYPE: this.rowData?.CALC_TYPE ?? '',
      IS_ACTIVE: this.rowData?.IS_ACTIVE ?? ''
    };
    const params: Record<string, any> = {
      request_json: JSON.stringify(requestPayload),
      tax_year: requestPayload.TAX_YEAR,
      jcd_key: f.jcd_key ?? g.jcd_key ?? 250,
      issue_id: issueId ?? '',
      issue_short_desc: f.issue_short_desc ?? g.issue_short_desc ?? '',
      checkLockedbj: '[{}]'
    };

    this.efileAdminService.saveJsonObject(
      this.GENERAL_CONFIG?.base_url ?? '',
      33453,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.saving = false;
        this.cdr.detectChanges();
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = (data?.errorMessage && data.errorMessage !== 'null') ? data.errorMessage : 'Error saving.';
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.saving = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    });
  }
}
